"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { documentEnTexte, nomDuFichier } from "@/lib/tools/documentText";
import { telechargerDocument } from "@/lib/tools/documentPdf";
import {
    aLImpotSurLesSocietes,
    affectationDe,
    composerDecision,
    limiteMiseEnPaiement,
    valider,
} from "@/lib/tools/soleShareholderDecision";
import { ApercuDocument } from "./DocumentPaper";
import { PadSignature, useSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Ecran,
    Liste,
    Question,
    Recommencer,
    Resultat,
    Segments,
    euros,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * La décision annuelle de l'associé unique : approbation des comptes et
 * affectation du résultat.
 *
 * LE LECTEUR DONNE SES CHIFFRES, LE DOCUMENT POSE LES MONTANTS DE LA LOI. La
 * réserve légale, le bénéfice distribuable et le report à nouveau se calculent
 * sous ses yeux, dès l'écran des réserves : il voit ce qu'il peut se verser
 * avant d'avoir à le décider.
 *
 * LA SIGNATURE EST LA SIENNE. C'est l'associé unique qui décide et qui signe :
 * le pavé reprend donc la signature gardée le temps de la visite, comme les
 * autres documents qu'il signe lui-même.
 *
 * PLUSIEURS ASSOCIÉS, UN REFUS. Une société à plusieurs associés statue en
 * assemblée, avec convocation, feuille de présence et vote : ce document ne
 * la remplace pas, et l'écran le dit.
 */

const PHASES = {
    forme: 0,
    plusieurs: 0,
    dirigeant: 0,
    fiscal: 0,
    societe: 0,
    siege: 0,
    associe: 0,
    exercice: 1,
    reserves: 1,
    historique: 1,
    dividendes: 1,
    conventions: 2,
    signature: 2,
    resultat: 2,
};

const VIDE = {
    forme: "",
    dirigeant: "",
    fiscal: "",
    denomination: "",
    capital: "",
    siren: "",
    villeRcs: "",
    voie: "",
    complement: "",
    codePostal: "",
    commune: "",
    civilite: "",
    prenoms: "",
    nom: "",
    adresseAssocie: "",
    cloture: "",
    sens: "benefice",
    resultat: "",
    reserveLegale: "",
    sensReport: "aucun",
    reportANouveau: "",
    exercicesPrecedents: "",
    dividendesN1: "",
    dividendesN2: "",
    dividendesN3: "",
    dividendes: "",
    miseEnPaiement: "",
    conventions: "non",
    conventionsTexte: "",
    lieuDeSignature: "",
    dateDeSignature: "",
};

const L = {
    fr: {
        phases: ["La société", "L'exercice", "La signature"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : le document s'y fabrique, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",
        forme: {
            titre: "Quelle est votre société ?",
            aide: "Ce document sert quand vous êtes le seul associé. C'est vous qui approuvez les comptes et qui décidez quoi faire du résultat.",
            choix: [
                { cle: "eurl", icone: "buildings-2-linear", titre: "EURL", detail: "Une SARL dont vous êtes le seul associé." },
                { cle: "sasu", icone: "user-id-linear", titre: "SASU", detail: "Une SAS dont vous êtes le seul associé." },
                { cle: "plusieurs", icone: "users-group-rounded-linear", titre: "Nous sommes plusieurs associés", detail: "SARL, SAS ou SCI à deux associés ou plus." },
            ],
        },
        plusieurs: {
            titre: "À plusieurs, vous approuvez les comptes en assemblée",
            aide: "Les associés sont convoqués, votent, et le procès-verbal reprend ce vote. Ce document, lui, ne vaut que pour un associé seul qui décide par écrit.",
            points: [
                "Relisez vos statuts : ils disent qui convoque l'assemblée, dans quel délai, et comment chacun peut voter.",
                "Votre expert-comptable prépare souvent le procès-verbal avec les comptes : demandez-le-lui.",
            ],
        },
        dirigeant: {
            titreEurl: "Êtes-vous aussi le gérant de votre EURL ?",
            titreSasu: "Êtes-vous aussi le président de votre SASU ?",
            aide: "C'est le cas de presque tous les solopreneurs. La réponse change une formalité après la signature.",
            oui: "Oui, c'est moi",
            ouiDetail: "Je suis à la fois l'associé et le dirigeant.",
            non: "Non, quelqu'un d'autre",
            nonDetail: "Un tiers dirige la société.",
        },
        fiscal: {
            titre: "Votre EURL est-elle à l'impôt sur les sociétés ?",
            aide: "Sans option, une EURL dont l'associé est une personne physique reste à l'impôt sur le revenu. La réponse change une mention obligatoire du document.",
            is: "Oui, elle est à l'IS",
            isDetail: "Vous vous versez une rémunération, et le bénéfice paie l'impôt sur les sociétés.",
            ir: "Non, elle est à l'impôt sur le revenu",
            irDetail: "Son bénéfice est imposé directement à votre nom.",
        },
        historique: {
            titre: "Vous êtes-vous versé des dividendes les années précédentes ?",
            aide: "La loi fiscale demande de rappeler, dans cette décision, les dividendes des trois exercices précédents. Ils figurent dans vos décisions des années passées.",
            nombre: "Exercices clos avant celui-ci",
            aucun: "Aucun",
            un: "Un",
            deux: "Deux",
            trois: "Trois ou plus",
            montant: "Dividendes de l'exercice clos en {annee}, en euros",
            montantAide: "Laissez vide s'il n'y en a pas eu.",
            suivant: "Continuer",
        },
        societe: {
            titre: "Votre société",
            aide: "Reprenez les informations de votre extrait Kbis : elles doivent être identiques.",
            denomination: "Dénomination sociale",
            capital: "Capital social, en euros",
            siren: "Numéro SIREN",
            sirenAide: "Neuf chiffres.",
            villeRcs: "Ville du registre du commerce",
            villeRcsAide: "Celle qui suit « RCS » sur votre Kbis.",
            suivant: "Continuer",
        },
        siege: {
            titre: "L'adresse du siège social",
            aide: "Celle de vos statuts et de votre Kbis.",
            voie: "Numéro et voie",
            complement: "Complément d'adresse",
            complementAide: "Facultatif. Bâtiment, étage, lieu-dit.",
            codePostal: "Code postal",
            commune: "Commune",
            suivant: "Continuer",
        },
        associe: {
            titre: "Vous, l'associé unique",
            aide: "Votre état civil et votre adresse personnelle, tels qu'ils figurent dans les statuts.",
            madame: "Madame",
            monsieur: "Monsieur",
            prenoms: "Prénoms",
            nom: "Nom",
            adresse: "Votre adresse personnelle",
            adresseAide: "Sur une seule ligne : numéro, voie, code postal, commune.",
            suivant: "Continuer",
        },
        exercice: {
            titre: "L'exercice que vous approuvez",
            aide: "Les chiffres de vos comptes annuels, tels que votre expert-comptable vous les a remis. Vous approuvez un exercice déjà terminé.",
            cloture: "Date de clôture de l'exercice terminé",
            clotureFuture: "Cette date n'est pas encore passée. Indiquez la fin de l'exercice terminé, le 31 décembre {annee} pour la plupart des sociétés.",
            benefice: "Un bénéfice",
            perte: "Une perte",
            resultat: "Montant du résultat, en euros",
            resultatAide: "Le résultat net comptable, en bas du compte de résultat.",
            suivant: "Continuer",
        },
        reserves: {
            titre: "Vos réserves et votre report à nouveau",
            aide: "Ils figurent au passif de votre bilan, dans les capitaux propres. La première année, les deux sont à zéro.",
            reserveLegale: "Réserve légale déjà constituée, en euros",
            report: "Report à nouveau avant cette décision",
            aucun: "Aucun",
            beneficiaire: "Bénéficiaire",
            deficitaire: "Déficitaire",
            reportMontant: "Montant du report à nouveau, en euros",
            calculTitre: "Ce que la loi impose",
            dotation: "Vous devez mettre {montant} à la réserve légale cette année.",
            pasDeDotation: "Votre réserve légale a atteint le dixième du capital : vous n'avez plus à l'alimenter.",
            distribuable: "Vous pouvez vous verser jusqu'à {montant} de dividendes.",
            rienDistribuable: "Cette année, vous ne pouvez pas vous verser de dividendes : le bénéfice ne couvre pas les pertes passées.",
            perte: "Votre perte ira au report à nouveau. Aucun dividende ne se verse sur cet exercice.",
            suivant: "Continuer",
        },
        dividendes: {
            titre: "Voulez-vous vous verser des dividendes ?",
            aide: "Laissez zéro si vous gardez tout dans la société. Ce qui n'est pas distribué va au report à nouveau.",
            montant: "Dividendes, en euros",
            maximum: "Au plus {montant}.",
            date: "Date de mise en paiement",
            dateAide: "Au plus tard le {limite}, neuf mois après la clôture.",
            suivant: "Continuer",
        },
        conventions: {
            titre: "Votre société a-t-elle signé un accord avec vous cette année ?",
            aide: "Un bail pour un bureau chez vous, un prêt, un compte courant rémunéré, un contrat de prestation. Votre rémunération de dirigeant n'en fait pas partie.",
            non: "Aucun accord",
            oui: "Oui",
            texte: "Décrivez chaque accord en une phrase",
            texteAide: "Par exemple : bail de la pièce de mon domicile qui sert de bureau, pour 300 euros par mois.",
            suivant: "Continuer",
        },
        signature: {
            titre: "Où et quand signez-vous ?",
            aide: "Dans les six mois qui suivent la clôture de l'exercice.",
            lieu: "Lieu de signature",
            date: "Date de signature",
            dessin: "Votre signature",
            dessinAide: "Facultative. Tracez-la à la souris ou au doigt, ou laissez l'emplacement vide pour signer à la main après impression. Elle sera reprise par vos autres documents de la visite.",
            reprise: "Reprise de votre document précédent. Retracez-la par-dessus ou effacez-la si ce n'est pas la bonne.",
            effacer: "Effacer",
            voir: "Voir mon document",
            avantCloture: "La décision se signe après la fin de l'exercice, le {cloture} : choisissez une date plus tardive.",
        },
        resultat: {
            titre: "Votre décision",
            apercu: "Aperçu du document",
            telecharger: "Télécharger en PDF",
            copier: "Copier le texte",
            copie: "Texte copié",
            manquesTitre: "Il manque encore quelque chose",
            manquesAide: "Complétez les champs signalés pour obtenir votre document.",
            corriger: "Corriger",
            verifierTitre: "Une valeur à vérifier",
            verifierAide: "Ces champs sont remplis, mais leur contenu ne passe pas. Corrigez-les et revenez ici.",
            motifs: {
                format: "le format n'est pas reconnu",
                positif: "le montant doit être supérieur à zéro",
                apresSignature: "elle tombe après la date de signature. Indiquez la fin de l'exercice terminé dont vous approuvez les comptes, pas celle de l'exercice en cours.",
                tropTard: "la mise en paiement dépasse les neuf mois qui suivent la clôture",
                avantDecision: "la mise en paiement ne peut pas précéder la décision",
            },
            alerteRamenes: "Vous aviez demandé plus que ce que vous pouvez distribuer : le document retient {montant}, le maximum permis.",
            alerteHorsDelai: "Vous signez plus de six mois après la clôture. La décision reste valable, mais le délai légal est dépassé : déposez vos comptes au plus vite.",
            suiteTitre: "Et maintenant",
            suites: {
                registreEurl: "Signez le document et consignez-le dans votre registre des décisions. En EURL, ce registre doit être coté et paraphé : la mairie le fait gratuitement.",
                registreSasu: "Signez le document et consignez-le dans votre registre des décisions. En SASU, le faire coter et parapher n'est pas obligatoire, mais c'est recommandé.",
                depot: "Déposez vos comptes annuels et cette décision au greffe dans le mois qui suit, deux mois si vous passez par le guichet des formalités en ligne.",
                depotDirigeant: "Comme vous êtes aussi le dirigeant, déposer vos comptes et l'inventaire dans les six mois suffirait à les approuver. La décision d'affectation, elle, reste obligatoire : c'est elle que contient ce document.",
                confidentialite: "Si votre société ne dépasse pas deux de ces trois seuils, 450 000 € de bilan, 900 000 € de chiffre d'affaires et 10 salariés, joignez à votre dépôt la déclaration de confidentialité : vos comptes ne seront pas publiés.",
                dividendes: "Dans les quinze premiers jours du mois qui suit le paiement des dividendes, votre société déclare et reverse l'acompte d'impôt de 12,8 % et les prélèvements sociaux, avec le formulaire 2777.",
                dispense: "Si votre revenu fiscal de référence est sous 50 000 €, 75 000 € en couple, vous pouvez demander à être dispensé de l'acompte de 12,8 % : l'attestation se remet à votre société au plus tard le 30 novembre de l'année d'avant.",
            },
            alerteMoitie: "Vos capitaux propres semblent tombés sous la moitié du capital. Dans les quatre mois qui suivent cette approbation, vous devez décider, par une décision à part, si la société continue.",
            fondTitre: "Pourquoi le document ne vous donne pas quitus",
            fond: "Beaucoup de modèles font écrire à l'associé qu'il « donne quitus au gérant ». La formule n'a aucun effet : la loi interdit qu'une décision des associés éteigne une action en responsabilité contre le dirigeant. Elle ne vous protège de rien, alors nous ne l'écrivons pas.",
            docEnFrancais: "",
            recommencer: "Recommencer",
        },
        manques: {
            forme: "La forme de votre société",
            denomination: "La dénomination sociale",
            capital: "Le capital social",
            siren: "Le numéro SIREN",
            villeRcs: "La ville du registre du commerce",
            voie: "L'adresse du siège",
            codePostal: "Le code postal du siège",
            commune: "La commune du siège",
            civilite: "Votre civilité",
            prenoms: "Vos prénoms",
            nom: "Votre nom",
            adresseAssocie: "Votre adresse personnelle",
            cloture: "La date de clôture",
            resultat: "Le montant du résultat",
            miseEnPaiement: "La date de mise en paiement des dividendes",
            exercicesPrecedents: "Le nombre d'exercices clos avant celui-ci",
            conventionsTexte: "La description des accords",
            lieuDeSignature: "Le lieu de signature",
            dateDeSignature: "La date de signature",
        },
    },
    en: {
        phases: ["The company", "The financial year", "Signing"],
        retour: "Back",
        prive: "Everything happens on your device: the document is built there, nobody receives your answers, not even us, and it all disappears with the tab.",
        forme: {
            titre: "What is your company?",
            aide: "This document is for when you are the only shareholder. You are the one who approves the accounts and decides what to do with the result.",
            choix: [
                { cle: "eurl", icone: "buildings-2-linear", titre: "EURL", detail: "A SARL of which you are the only shareholder." },
                { cle: "sasu", icone: "user-id-linear", titre: "SASU", detail: "A SAS of which you are the only shareholder." },
                { cle: "plusieurs", icone: "users-group-rounded-linear", titre: "We are several shareholders", detail: "SARL, SAS or SCI with two shareholders or more." },
            ],
        },
        plusieurs: {
            titre: "With several shareholders, you approve the accounts at a general meeting",
            aide: "The shareholders are convened, they vote, and the minutes record that vote. This document only works for a single shareholder deciding in writing.",
            points: [
                "Read your articles of association again: they say who calls the meeting, with how much notice, and how each person can vote.",
                "Your accountant often prepares the minutes along with the accounts: ask them.",
            ],
        },
        dirigeant: {
            titreEurl: "Are you also the manager of your EURL?",
            titreSasu: "Are you also the president of your SASU?",
            aide: "That is the case for almost every solopreneur. The answer changes one formality after signing.",
            oui: "Yes, it is me",
            ouiDetail: "I am both the shareholder and the director.",
            non: "No, someone else",
            nonDetail: "A third party runs the company.",
        },
        fiscal: {
            titre: "Is your EURL under corporate tax?",
            aide: "Without the option, an EURL whose shareholder is an individual stays under income tax. The answer changes one required statement in the document.",
            is: "Yes, it is under corporate tax",
            isDetail: "You pay yourself as a manager, and the profit pays corporate tax.",
            ir: "No, it is under income tax",
            irDetail: "Its profit is taxed directly in your name.",
        },
        historique: {
            titre: "Did you pay yourself dividends in previous years?",
            aide: "Tax law requires this decision to recall the dividends of the three previous financial years. They appear in your decisions from past years.",
            nombre: "Financial years closed before this one",
            aucun: "None",
            un: "One",
            deux: "Two",
            trois: "Three or more",
            montant: "Dividends for the year closed in {annee}, in euros",
            montantAide: "Leave empty if there were none.",
            suivant: "Continue",
        },
        societe: {
            titre: "Your company",
            aide: "Copy the details from your Kbis extract: they must be identical.",
            denomination: "Company name",
            capital: "Share capital, in euros",
            siren: "SIREN number",
            sirenAide: "Nine digits.",
            villeRcs: "Town of the trade register",
            villeRcsAide: "The one after “RCS” on your Kbis.",
            suivant: "Continue",
        },
        siege: {
            titre: "The registered office address",
            aide: "The one in your articles and on your Kbis.",
            voie: "Number and street",
            complement: "Address line 2",
            complementAide: "Optional. Building, floor, locality.",
            codePostal: "Postcode",
            commune: "Town",
            suivant: "Continue",
        },
        associe: {
            titre: "You, the sole shareholder",
            aide: "Your civil status and personal address, as they appear in the articles.",
            madame: "Ms",
            monsieur: "Mr",
            prenoms: "Forenames",
            nom: "Surname",
            adresse: "Your personal address",
            adresseAide: "On a single line: number, street, postcode, town.",
            suivant: "Continue",
        },
        exercice: {
            titre: "The financial year you are approving",
            aide: "The figures from your annual accounts, as your accountant gave them to you. You are approving a financial year that has ended.",
            cloture: "Year-end date of the completed financial year",
            clotureFuture: "This date has not passed yet. Enter the end of the completed financial year, 31 December {annee} for most companies.",
            benefice: "A profit",
            perte: "A loss",
            resultat: "Amount of the result, in euros",
            resultatAide: "The net accounting result, at the bottom of the income statement.",
            suivant: "Continue",
        },
        reserves: {
            titre: "Your reserves and your retained earnings",
            aide: "They appear on the liabilities side of your balance sheet, under equity. In the first year, both are zero.",
            reserveLegale: "Legal reserve already built up, in euros",
            report: "Retained earnings before this decision",
            aucun: "None",
            beneficiaire: "Positive",
            deficitaire: "Negative",
            reportMontant: "Amount of retained earnings, in euros",
            calculTitre: "What the law requires",
            dotation: "You must put {montant} into the legal reserve this year.",
            pasDeDotation: "Your legal reserve has reached a tenth of the capital: you no longer need to top it up.",
            distribuable: "You can pay yourself up to {montant} in dividends.",
            rienDistribuable: "This year, you cannot pay yourself dividends: the profit does not cover past losses.",
            perte: "Your loss goes to retained earnings. No dividend is paid on this financial year.",
            suivant: "Continue",
        },
        dividendes: {
            titre: "Do you want to pay yourself dividends?",
            aide: "Leave zero if you keep everything in the company. What is not distributed goes to retained earnings.",
            montant: "Dividends, in euros",
            maximum: "At most {montant}.",
            date: "Payment date",
            dateAide: "By {limite} at the latest, nine months after year end.",
            suivant: "Continue",
        },
        conventions: {
            titre: "Did your company sign an agreement with you this year?",
            aide: "A lease for an office in your home, a loan, an interest-bearing current account, a service contract. Your pay as a director does not count.",
            non: "No agreement",
            oui: "Yes",
            texte: "Describe each agreement in one sentence",
            texteAide: "The document stays in French: for example, « bail de la pièce de mon domicile qui sert de bureau, pour 300 euros par mois ».",
            suivant: "Continue",
        },
        signature: {
            titre: "Where and when are you signing?",
            aide: "Within six months of the financial year end.",
            lieu: "Place of signature",
            date: "Date of signature",
            dessin: "Your signature",
            dessinAide: "Optional. Draw it with the mouse or your finger, or leave the space empty and sign by hand after printing. Your other documents in this visit will reuse it.",
            reprise: "Taken from your previous document. Draw over it, or clear it if it is not the right one.",
            effacer: "Clear",
            voir: "See my document",
            avantCloture: "The decision is signed after the year end, {cloture}: choose a later date.",
        },
        resultat: {
            titre: "Your decision",
            apercu: "Document preview",
            telecharger: "Download as PDF",
            copier: "Copy the text",
            copie: "Text copied",
            manquesTitre: "Something is still missing",
            manquesAide: "Fill in the fields listed to get your document.",
            corriger: "Fix",
            verifierTitre: "A value to check",
            verifierAide: "These fields are filled in, but their content does not pass. Correct them and come back here.",
            motifs: {
                format: "the format is not recognised",
                positif: "the amount must be above zero",
                apresSignature: "it falls after the signing date. Enter the end of the completed financial year whose accounts you approve, not the current one.",
                tropTard: "the payment date is more than nine months after year end",
                avantDecision: "the payment date cannot come before the decision",
            },
            alerteRamenes: "You asked for more than you can distribute: the document keeps {montant}, the maximum allowed.",
            alerteHorsDelai: "You are signing more than six months after year end. The decision remains valid, but the legal deadline has passed: file your accounts as soon as you can.",
            suiteTitre: "What next",
            suites: {
                registreEurl: "Sign the document and record it in your register of decisions. In an EURL, that register must be numbered and initialled: the town hall does it for free.",
                registreSasu: "Sign the document and record it in your register of decisions. In a SASU, having it numbered and initialled is not compulsory, but it is recommended.",
                depot: "File your annual accounts and this decision with the registry within the following month, two months if you use the online formalities portal.",
                depotDirigeant: "As you are also the director, filing your accounts and the inventory within six months would be enough to approve them. The allocation decision remains compulsory: that is what this document contains.",
                confidentialite: "If your company does not exceed two of these three thresholds, €450,000 of balance sheet, €900,000 of turnover and 10 employees, attach the confidentiality declaration to your filing: your accounts will not be published.",
                dividendes: "Within the first fifteen days of the month after the dividends are paid, your company declares and pays the 12.8% income tax instalment and the social levies, with form 2777.",
                dispense: "If your reference tax income is below €50,000, €75,000 as a couple, you can ask to be exempted from the 12.8% instalment: the certificate goes to your company by 30 November of the previous year.",
            },
            alerteMoitie: "Your equity seems to have fallen below half of the share capital. Within four months of this approval, you must decide, in a separate decision, whether the company carries on.",
            fondTitre: "Why the document does not grant you discharge",
            fond: "Many templates have the shareholder write that they “give discharge to the manager”. The phrase has no effect: the law forbids a shareholders' decision from extinguishing a liability claim against the director. It protects you from nothing, so we do not write it.",
            docEnFrancais: "The document below stays in French: it is filed with the French trade registry.",
            recommencer: "Start again",
        },
        manques: {
            forme: "Your company form",
            denomination: "The company name",
            capital: "The share capital",
            siren: "The SIREN number",
            villeRcs: "The town of the trade register",
            voie: "The registered office address",
            codePostal: "The registered office postcode",
            commune: "The registered office town",
            civilite: "Your title",
            prenoms: "Your forenames",
            nom: "Your surname",
            adresseAssocie: "Your personal address",
            cloture: "The year-end date",
            resultat: "The amount of the result",
            miseEnPaiement: "The dividend payment date",
            exercicesPrecedents: "The number of financial years closed before this one",
            conventionsTexte: "The description of the agreements",
            lieuDeSignature: "The place of signature",
            dateDeSignature: "The date of signature",
        },
    },
};

export default function DecisionAssocieUnique({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;
    const jour = String(aujourdhui).slice(0, 10);
    const annee = Number(jour.slice(0, 4));
    // La plupart des sociétés clôturent au 31 décembre, et l'on approuve au
    // printemps l'exercice de l'année d'avant : c'est le bon point de départ.
    // L'identité gardée ne remplit que l'associé : la société (siège compris)
    // n'en vient jamais.
    const { identite } = useDossierOutil();
    const vide = { ...VIDE, cloture: `${annee - 1}-12-31`, ...identite };
    const [rep, setRep] = useState(vide);
    const [copie, setCopie] = useState(false);
    const { signature, poser: poserSignature } = useSignature();

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "forme",
        () => {
            setRep(vide);
            setCopie(false);
        },
        PHASES,
    );

    useSignalerIdentite(
        { civilite: rep.civilite, prenoms: rep.prenoms, nom: rep.nom },
        ["exercice", "reserves", "historique", "dividendes", "conventions", "signature", "resultat"].includes(etape),
    );

    const poser = (cle) => (valeur) => setRep((p) => ({ ...p, [cle]: valeur }));
    const clotureFuture = /^\d{4}-\d{2}-\d{2}$/.test(rep.cloture) && rep.cloture > jour;
    const reponses = useMemo(() => ({ ...rep, dateDeSignature: rep.dateDeSignature || jour }), [rep, jour]);
    const controle = useMemo(() => valider(reponses), [reponses]);
    const decision = useMemo(() => composerDecision(reponses), [reponses]);
    const affectation = useMemo(() => affectationDe(reponses), [reponses]);
    const rempli = (...cles) => cles.every((c) => String(rep[c] || "").trim());
    const sommes = (v) => euros(v, lang);

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang, onRetour: retour };
    const refusDe = (champ) => (controle.invalides || []).find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        const refus = refusDe(champ);
        return refus ? r.motifs[refus.motif] || r.motifs.format : "";
    };

    const telecharger = () =>
        telechargerDocument({
            document: decision,
            signature,
            nomFichier: nomDuFichier("decisions-associe-unique", { prenoms: rep.prenoms, nom: rep.nom }),
        });

    const copier = async () => {
        if (!decision) return;
        try {
            await navigator.clipboard.writeText(documentEnTexte(decision));
            setCopie(true);
            window.setTimeout(() => setCopie(false), 2000);
        } catch {
            // Presse-papiers refusé : le PDF reste la voie normale.
        }
    };

    if (etape === "forme") {
        return (
            <Question
                key="forme"
                {...commun}
                phase={PHASES.forme}
                titre={t.forme.titre}
                aide={t.forme.aide}
                choix={t.forme.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        if (c.cle === "plusieurs") {
                            aller("plusieurs");
                            return;
                        }
                        setRep((p) => ({ ...p, forme: c.cle }));
                        aller("dirigeant");
                    },
                }))}
            />
        );
    }

    if (etape === "plusieurs") {
        return (
            <Ecran key="plusieurs" {...commun} phase={PHASES.plusieurs} titre={t.plusieurs.titre} aide={t.plusieurs.aide}>
                <Liste items={t.plusieurs.points} />
                <Recommencer onClick={recommencer} libelle={r.recommencer} />
            </Ecran>
        );
    }

    if (etape === "dirigeant") {
        return (
            <Question
                key="dirigeant"
                {...commun}
                phase={PHASES.dirigeant}
                titre={rep.forme === "sasu" ? t.dirigeant.titreSasu : t.dirigeant.titreEurl}
                aide={t.dirigeant.aide}
                choix={[
                    { cle: "oui", icone: "user-check-linear", titre: t.dirigeant.oui, detail: t.dirigeant.ouiDetail },
                    { cle: "non", icone: "users-group-two-rounded-linear", titre: t.dirigeant.non, detail: t.dirigeant.nonDetail },
                ].map((c) => ({
                    ...c,
                    onClick: () => {
                        // Une SASU est à l'IS ; une EURL, seulement sur option.
                        setRep((p) => ({ ...p, dirigeant: c.cle, fiscal: p.forme === "sasu" ? "is" : p.fiscal }));
                        aller(rep.forme === "eurl" ? "fiscal" : "societe");
                    },
                }))}
            />
        );
    }

    if (etape === "fiscal") {
        return (
            <Question
                key="fiscal"
                {...commun}
                phase={PHASES.fiscal}
                titre={t.fiscal.titre}
                aide={t.fiscal.aide}
                choix={[
                    { cle: "is", icone: "safe-square-linear", titre: t.fiscal.is, detail: t.fiscal.isDetail },
                    { cle: "ir", icone: "document-text-linear", titre: t.fiscal.ir, detail: t.fiscal.irDetail },
                ].map((c) => ({
                    ...c,
                    onClick: () => {
                        setRep((p) => ({ ...p, fiscal: c.cle }));
                        aller("societe");
                    },
                }))}
            />
        );
    }

    if (etape === "historique") {
        const nombre = Number(rep.exercicesPrecedents) || 0;
        const annee = rep.cloture ? Number(rep.cloture.slice(0, 4)) : null;
        return (
            <Ecran key="historique" {...commun} phase={PHASES.historique} titre={t.historique.titre} aide={t.historique.aide}>
                <div className="space-y-5">
                    <div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mb-2">{t.historique.nombre}</div>
                        <Segments
                            options={[
                                { cle: "0", libelle: t.historique.aucun },
                                { cle: "1", libelle: t.historique.un },
                                { cle: "2", libelle: t.historique.deux },
                                { cle: "3", libelle: t.historique.trois },
                            ]}
                            valeur={rep.exercicesPrecedents}
                            poser={poser("exercicesPrecedents")}
                        />
                    </div>
                    {[1, 2, 3].slice(0, nombre).map((i) => (
                        <Champ
                            key={i}
                            libelle={remplir(t.historique.montant, { annee: annee ? annee - i : "" })}
                            aide={t.historique.montantAide}
                            valeur={rep[`dividendesN${i}`]}
                            poser={poser(`dividendesN${i}`)}
                            inputMode="decimal"
                        />
                    ))}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rep.exercicesPrecedents}
                    onClick={() => aller(affectation && !affectation.perte && affectation.distribuable > 0 ? "dividendes" : "conventions")}
                >
                    {t.historique.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "societe") {
        return (
            <Ecran key="societe" {...commun} phase={PHASES.societe} titre={t.societe.titre} aide={t.societe.aide}>
                <div className="space-y-5">
                    <Champ libelle={t.societe.denomination} valeur={rep.denomination} poser={poser("denomination")} />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ
                            libelle={t.societe.capital}
                            valeur={rep.capital}
                            poser={poser("capital")}
                            inputMode="decimal"
                            erreur={erreurDe("capital")}
                        />
                        <Champ
                            libelle={t.societe.siren}
                            aide={t.societe.sirenAide}
                            valeur={rep.siren}
                            poser={poser("siren")}
                            inputMode="numeric"
                            erreur={erreurDe("siren")}
                        />
                    </div>
                    <Champ
                        libelle={t.societe.villeRcs}
                        aide={t.societe.villeRcsAide}
                        valeur={rep.villeRcs}
                        poser={poser("villeRcs")}
                    />
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("denomination", "capital", "siren", "villeRcs") || Boolean(refusDe("siren")) || Boolean(refusDe("capital"))}
                    onClick={() => aller("siege")}
                >
                    {t.societe.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "siege") {
        return (
            <Ecran key="siege" {...commun} phase={PHASES.siege} titre={t.siege.titre} aide={t.siege.aide}>
                <div className="space-y-5">
                    <Champ libelle={t.siege.voie} valeur={rep.voie} poser={poser("voie")} />
                    <Champ libelle={t.siege.complement} aide={t.siege.complementAide} valeur={rep.complement} poser={poser("complement")} />
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Champ libelle={t.siege.codePostal} valeur={rep.codePostal} poser={poser("codePostal")} inputMode="numeric" />
                        <div className="sm:col-span-2">
                            <Champ libelle={t.siege.commune} valeur={rep.commune} poser={poser("commune")} />
                        </div>
                    </div>
                </div>
                <Avancer pleineLargeur disabled={!rempli("voie", "codePostal", "commune")} onClick={() => aller("associe")}>
                    {t.siege.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "associe") {
        return (
            <Ecran key="associe" {...commun} phase={PHASES.associe} titre={t.associe.titre} aide={t.associe.aide}>
                <div className="space-y-5">
                    <Segments
                        options={[
                            { cle: "madame", libelle: t.associe.madame },
                            { cle: "monsieur", libelle: t.associe.monsieur },
                        ]}
                        valeur={rep.civilite}
                        poser={poser("civilite")}
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.associe.prenoms} valeur={rep.prenoms} poser={poser("prenoms")} />
                        <Champ libelle={t.associe.nom} valeur={rep.nom} poser={poser("nom")} />
                    </div>
                    <Champ
                        libelle={t.associe.adresse}
                        aide={t.associe.adresseAide}
                        valeur={rep.adresseAssocie}
                        poser={poser("adresseAssocie")}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("civilite", "prenoms", "nom", "adresseAssocie")}
                    onClick={() => aller("exercice")}
                >
                    {t.associe.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "exercice") {
        return (
            <Ecran key="exercice" {...commun} phase={PHASES.exercice} titre={t.exercice.titre} aide={t.exercice.aide}>
                <div className="space-y-5">
                    <Champ
                        type="date"
                        libelle={t.exercice.cloture}
                        valeur={rep.cloture}
                        poser={poser("cloture")}
                        erreur={clotureFuture ? "" : erreurDe("cloture")}
                    />
                    {clotureFuture && (
                        <p className="text-sm text-amber-700 dark:text-amber-400 -mt-2 mb-0">
                            {remplir(t.exercice.clotureFuture, { annee: annee - 1 })}
                        </p>
                    )}
                    <Segments
                        options={[
                            { cle: "benefice", libelle: t.exercice.benefice },
                            { cle: "perte", libelle: t.exercice.perte },
                        ]}
                        valeur={rep.sens}
                        poser={poser("sens")}
                    />
                    <Champ
                        libelle={t.exercice.resultat}
                        aide={t.exercice.resultatAide}
                        valeur={rep.resultat}
                        poser={poser("resultat")}
                        inputMode="decimal"
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("cloture", "resultat") || clotureFuture || refusDe("cloture")?.motif === "format"}
                    onClick={() => aller("reserves")}
                >
                    {t.exercice.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "reserves") {
        const a = affectation;
        return (
            <Ecran key="reserves" {...commun} phase={PHASES.reserves} titre={t.reserves.titre} aide={t.reserves.aide}>
                <div className="space-y-5">
                    <Champ
                        libelle={t.reserves.reserveLegale}
                        valeur={rep.reserveLegale}
                        poser={poser("reserveLegale")}
                        inputMode="decimal"
                    />
                    <div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mb-2">{t.reserves.report}</div>
                        <Segments
                            options={[
                                { cle: "aucun", libelle: t.reserves.aucun },
                                { cle: "benefice", libelle: t.reserves.beneficiaire },
                                { cle: "perte", libelle: t.reserves.deficitaire },
                            ]}
                            valeur={rep.sensReport}
                            poser={(v) => setRep((p) => ({ ...p, sensReport: v, reportANouveau: v === "aucun" ? "" : p.reportANouveau }))}
                        />
                    </div>
                    {rep.sensReport !== "aucun" && (
                        <Champ
                            libelle={t.reserves.reportMontant}
                            valeur={rep.reportANouveau}
                            poser={poser("reportANouveau")}
                            inputMode="decimal"
                        />
                    )}
                </div>
                {a && (
                    <div className="mt-6 rounded-xl border border-primary/30 bg-primary/[0.06] px-4 py-4">
                        <div className="font-semibold dark:text-white">{t.reserves.calculTitre}</div>
                        {a.perte ? (
                            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{t.reserves.perte}</p>
                        ) : (
                            <Liste
                                items={[
                                    a.dotation > 0
                                        ? remplir(t.reserves.dotation, { montant: sommes(a.dotation) })
                                        : t.reserves.pasDeDotation,
                                    a.distribuable > 0
                                        ? remplir(t.reserves.distribuable, { montant: sommes(a.distribuable) })
                                        : t.reserves.rienDistribuable,
                                ]}
                            />
                        )}
                    </div>
                )}
                <Avancer
                    pleineLargeur
                    onClick={() =>
                        aller(
                            aLImpotSurLesSocietes(rep)
                                ? "historique"
                                : affectation && !affectation.perte && affectation.distribuable > 0
                                  ? "dividendes"
                                  : "conventions",
                        )
                    }
                >
                    {t.reserves.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "dividendes") {
        const limite = limiteMiseEnPaiement(rep.cloture);
        return (
            <Ecran key="dividendes" {...commun} phase={PHASES.dividendes} titre={t.dividendes.titre} aide={t.dividendes.aide}>
                <div className="space-y-5">
                    <Champ
                        libelle={t.dividendes.montant}
                        aide={remplir(t.dividendes.maximum, { montant: sommes(affectation?.distribuable || 0) })}
                        valeur={rep.dividendes}
                        poser={poser("dividendes")}
                        inputMode="decimal"
                    />
                    {Number(String(rep.dividendes).replace(",", ".")) > 0 && (
                        <Champ
                            type="date"
                            libelle={t.dividendes.date}
                            aide={limite ? remplir(t.dividendes.dateAide, { limite: new Date(`${limite}T12:00:00Z`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }) }) : undefined}
                            valeur={rep.miseEnPaiement}
                            poser={poser("miseEnPaiement")}
                            erreur={erreurDe("miseEnPaiement")}
                        />
                    )}
                </div>
                <Avancer pleineLargeur onClick={() => aller("conventions")}>
                    {t.dividendes.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "conventions") {
        return (
            <Ecran key="conventions" {...commun} phase={PHASES.conventions} titre={t.conventions.titre} aide={t.conventions.aide}>
                <div className="space-y-5">
                    <Segments
                        options={[
                            { cle: "non", libelle: t.conventions.non },
                            { cle: "oui", libelle: t.conventions.oui },
                        ]}
                        valeur={rep.conventions}
                        poser={poser("conventions")}
                    />
                    {rep.conventions === "oui" && (
                        <Champ
                            libelle={t.conventions.texte}
                            aide={t.conventions.texteAide}
                            valeur={rep.conventionsTexte}
                            poser={poser("conventionsTexte")}
                        />
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={rep.conventions === "oui" && !rempli("conventionsTexte")}
                    onClick={() => aller("signature")}
                >
                    {t.conventions.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        // Une signature avant la fin de l'exercice approuvé : on le dit ici,
        // au lieu de le découvrir sur l'écran final.
        const signeeAvant =
            /^\d{4}-\d{2}-\d{2}$/.test(rep.cloture) && /^\d{4}-\d{2}-\d{2}$/.test(reponses.dateDeSignature) && reponses.dateDeSignature < rep.cloture;
        return (
            <Ecran key="signature" {...commun} phase={PHASES.signature} titre={t.signature.titre} aide={t.signature.aide}>
                <div className="space-y-5">
                    <Champ libelle={t.signature.lieu} valeur={rep.lieuDeSignature} poser={poser("lieuDeSignature")} />
                    <Champ
                        type="date"
                        libelle={t.signature.date}
                        valeur={rep.dateDeSignature || jour}
                        poser={poser("dateDeSignature")}
                        erreur={erreurDe("dateDeSignature")}
                    />
                    {signeeAvant && (
                        <p className="text-sm text-amber-700 dark:text-amber-400 -mt-2 mb-0">
                            {remplir(t.signature.avantCloture, {
                                cloture: new Date(`${rep.cloture}T12:00:00Z`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                    timeZone: "UTC",
                                }),
                            })}
                        </p>
                    )}
                    <PadSignature
                        libelle={t.signature.dessin}
                        aide={t.signature.dessinAide}
                        effacer={t.signature.effacer}
                        repriseLibelle={t.signature.reprise}
                        valeur={signature}
                        poser={poserSignature}
                    />
                </div>
                <Avancer pleineLargeur disabled={!rempli("lieuDeSignature") || signeeAvant} onClick={() => aller("resultat")}>
                    {t.signature.voir}
                </Avancer>
            </Ecran>
        );
    }

    // Chaque ligne d'erreur ramène à l'écran où le champ se corrige, au lieu
    // d'obliger à remonter tout le parcours à coups de « Retour ».
    const ECRAN_DU_CHAMP = {
        forme: "forme",
        denomination: "societe",
        capital: "societe",
        siren: "societe",
        villeRcs: "societe",
        voie: "siege",
        codePostal: "siege",
        commune: "siege",
        civilite: "associe",
        prenoms: "associe",
        nom: "associe",
        adresseAssocie: "associe",
        cloture: "exercice",
        resultat: "exercice",
        exercicesPrecedents: "historique",
        miseEnPaiement: "dividendes",
        conventionsTexte: "conventions",
        lieuDeSignature: "signature",
        dateDeSignature: "signature",
    };
    const corriger = (champ) =>
        ECRAN_DU_CHAMP[champ] ? (
            <button
                type="button"
                onClick={() => aller(ECRAN_DU_CHAMP[champ])}
                className="shrink-0 text-sm font-semibold text-primary hover:underline"
            >
                {r.corriger}
            </button>
        ) : null;

    const suites = [
        rep.forme === "sasu" ? r.suites.registreSasu : r.suites.registreEurl,
        r.suites.depot,
        ...(rep.dirigeant === "oui" ? [r.suites.depotDirigeant] : []),
        r.suites.confidentialite,
        ...(affectation?.dividendes > 0 && aLImpotSurLesSocietes(rep) ? [r.suites.dividendes, r.suites.dispense] : []),
    ];

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} sens={sens} reduit={reduit}>
            {!decision ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? r.manquesTitre : r.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? r.manquesAide : r.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span className="flex-1">{t.manques[cle] || cle}</span>
                                {corriger(cle)}
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif }) => (
                            <li key={`${champ}-${motif}`} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span className="flex-1">
                                    {t.manques[champ] || champ}
                                    {" : "}
                                    {r.motifs[motif] || r.motifs.format}
                                </span>
                                {corriger(champ)}
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    {controle.alertes.includes("dividendesRamenes") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-3">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {remplir(r.alerteRamenes, { montant: sommes(affectation.dividendes) })}
                            </p>
                        </div>
                    )}
                    {controle.alertes.includes("moitieCapital") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-3">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">{r.alerteMoitie}</p>
                        </div>
                    )}
                    {controle.alertes.includes("horsDelai") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-3">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">{r.alerteHorsDelai}</p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3 mt-2">
                        {r.apercu}
                    </div>
                    {r.docEnFrancais && <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-3">{r.docEnFrancais}</p>}

                    <ApercuDocument document={decision} signature={signature} />

                    <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={telecharger}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                        >
                            <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                            {r.telecharger}
                        </button>
                        <button
                            type="button"
                            onClick={copier}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue font-semibold transition-colors duration-200 hover:border-primary/40 hover:text-primary"
                        >
                            <SolarIcon name={copie ? "check-circle-linear" : "copy-linear"} className="text-lg" />
                            {copie ? r.copie : r.copier}
                        </button>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon name="clipboard-check-linear" className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <div className="font-bold dark:text-white leading-snug">{r.suiteTitre}</div>
                        </div>
                        <Liste items={suites} />
                    </div>

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon name="shield-linear" className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <div className="font-semibold dark:text-white leading-snug">{r.fondTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{r.fond}</p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={r.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
